/* Typed navigation: "change to maintenance", "take me to the live map".
   The words after the verb have to name a console screen. A question
   ("why is fuel high") is never a navigation. */

import { PAGES, PageKey } from '../kit';

export interface ScreenGo {
  page: PageKey;
  href: string;
  name: string;
}

const SCREENS: { re: RegExp; page: PageKey; name: string }[] = [
  { re: /\b(maintenance|maintence|maintanence|maint)\b/, page: 'ConsoleMaintenance', name: 'Maintenance' },
  { re: /\bfuel prices?\b/, page: 'ConsoleFuelPrices', name: 'Fuel prices' },
  { re: /\bfuel\b/, page: 'ConsoleFuel', name: 'Fuel' },
  { re: /\balerts?\b/, page: 'ConsoleAlerts', name: 'Alerts' },
  { re: /\blive map\b|\bthe map\b/, page: 'ConsoleLiveMap', name: 'Live map' },
  { re: /\bbuses\b/, page: 'ConsoleBuses', name: 'Buses' },
  { re: /\btrips\b/, page: 'ConsoleTrips', name: 'Trips' },
  { re: /\b(people|parents|drivers)\b/, page: 'ConsolePeople', name: 'Drivers and parents' },
  { re: /\bsettings\b/, page: 'ConsoleSettings', name: 'Settings' },
  { re: /\banalytics\b/, page: 'ConsoleAnalytics', name: 'Analytics' },
  { re: /\b(overview|home)\b/, page: 'ConsoleOverview', name: 'Overview' },
  { re: /\breports?\b/, page: 'ConsoleReport', name: 'Report' },
  { re: /\bbilling\b/, page: 'ConsoleBilling', name: 'Billing' },
];

/** The first console screen named in this text. */
export function screenNamed(text: string): ScreenGo | null {
  const t = text.toLowerCase();
  for (const s of SCREENS) {
    if (s.re.test(t)) return { page: s.page, href: PAGES[s.page], name: s.name };
  }
  return null;
}

/** Typos that were turning a walkthrough into "Alerts is open." */
export function tidyAsk(q: string): string {
  return q.toLowerCase()
    .replace(/\bthrouhg\b|\bthruogh\b|\bthorugh\b|\bthrugh\b|\bthrogh\b|\bthru\b/g, 'through')
    .replace(/\bmos\s+t(\w+)/g, 'most $1')
    .replace(/\s+/g, ' ')
    .trim();
}

/** "Take me through the alerts" is an explanation, even with the typos.
    A bare "take me to alerts" is not. */
export function walkThrough(q: string): { screen: ScreenGo | null; question: string } | null {
  const t = tidyAsk(q);
  if (!/\b(take|walk|talk) me through\b/.test(t)) return null;
  const screen = screenNamed(t);
  const where = screen ? `the ${screen.name} screen` : 'this page';
  const pressing = /\b(pressing|urgent|worst|serious|important)\b/.test(t);
  const question = pressing
    ? `Why these are the most pressing things on ${where}. Name each one, what is wrong, and the one next step. Do not stop at saying the screen is open.`
    : `Walk me through ${where}. Say what I am looking at, what matters, and the one next step. Do not stop at saying the screen is open.`;
  return { screen, question };
}

/** A command to open a screen. Null when the sentence is a question or names no screen. */
export function pageAsked(q: string): ScreenGo | null {
  const t = tidyAsk(q);
  if (/\b(why|explain|tour|through|understand|mean|simplif|summar)\b/.test(t)) return null;
  const m = t.match(/\b(?:take me(?: to)?|go to|open|change to|switch to|show me)(?: the| to)?\s+([a-z ]{3,40})/);
  if (!m) return null;
  return screenNamed(m[1]);
}
